// Pure pixel-buffer code with no DOM access, so it can move into a Lambda (ADR 0002).

export interface ReliefParams {
  reliefDepthMm: number;
  bevelWidthMm: number;
  occlusionRadiusMm: number;
  occlusionStrength: number;
  sunElevationDeg: number;
  sunAzimuthDeg: number;
  shadowSoftnessMm: number;
  shadowStrength: number;
  maxSunBoost: number;
}

// Tuned by eye in the widget preview.
export const RELIEF_DEFAULTS: ReliefParams = {
  reliefDepthMm: 2,
  bevelWidthMm: 2,
  occlusionRadiusMm: 10,
  occlusionStrength: 0.2,
  sunElevationDeg: 60,
  // Positive is from the left.
  sunAzimuthDeg: 15,
  shadowSoftnessMm: 2,
  shadowStrength: 0.3,
  maxSunBoost: 1.2,
};

export interface ReliefInput {
  width: number;
  height: number;
  pxPerMm: number;
  // 255 is brick, 0 is joint.
  mask: Uint8Array | Uint8ClampedArray;
  albedo: Uint8ClampedArray;
}

export interface ReliefMaps {
  // Relative height, 0 (joint) to 1 (brick face). Not part of the texture set.
  height: Float32Array;
  // OpenGL convention: green points up.
  normal: Uint8ClampedArray;
  occlusion: Uint8ClampedArray;
  diffuse: Uint8ClampedArray;
}

// Sums (not averages) over a (2r+1) window with wrap-around, so integer input
// stays exact and the output is identical for identical neighbourhoods.
const boxSumWrap = (
  src: Float64Array,
  width: number,
  height: number,
  r: number,
  horizontal: boolean
) => {
  const dst = new Float64Array(src.length);
  const length = horizontal ? width : height;
  const lines = horizontal ? height : width;
  const at = horizontal
    ? (line: number, i: number) => line * width + i
    : (line: number, i: number) => i * width + line;
  const wrap = (i: number) => ((i % length) + length) % length;
  for (let line = 0; line < lines; line++) {
    let sum = 0;
    for (let k = -r; k <= r; k++) sum += src[at(line, wrap(k))];
    for (let i = 0; i < length; i++) {
      dst[at(line, i)] = sum;
      sum += src[at(line, wrap(i + r + 1))] - src[at(line, wrap(i - r))];
    }
  }
  return dst;
};

const tentSumWrap = (src: Float64Array, width: number, height: number, r: number) => {
  let out = src;
  for (let pass = 0; pass < 2; pass++) {
    out = boxSumWrap(out, width, height, r, true);
    out = boxSumWrap(out, width, height, r, false);
  }
  return { sums: out, scale: (2 * r + 1) ** 4 };
};

const radiusPx = (mm: number, pxPerMm: number) => Math.max(1, Math.round((mm * pxPerMm) / 2));

export const computeReliefMaps = (
  { width, height, pxPerMm, mask, albedo }: ReliefInput,
  params: ReliefParams = RELIEF_DEFAULTS
): ReliefMaps => {
  const n = width * height;
  const px = (x: number, y: number) =>
    ((y + height) % height) * width + ((x + width) % width);

  const maskSums = new Float64Array(n);
  for (let i = 0; i < n; i++) maskSums[i] = mask[i];
  const bevel = tentSumWrap(maskSums, width, height, radiusPx(params.bevelWidthMm, pxPerMm));
  const heightScale = bevel.scale * 255;
  const heightSums = bevel.sums;

  const relief = new Float32Array(n);
  for (let i = 0; i < n; i++) relief[i] = heightSums[i] / heightScale;

  const elevation = (params.sunElevationDeg * Math.PI) / 180;
  const azimuth = (params.sunAzimuthDeg * Math.PI) / 180;
  const sun = {
    x: -Math.cos(elevation) * Math.sin(azimuth),
    y: Math.sin(elevation),
    z: Math.cos(elevation) * Math.cos(azimuth),
  };
  const depth = params.reliefDepthMm;
  const mmPerPx = 1 / pxPerMm;

  const normal = new Uint8ClampedArray(n * 4);
  const shade = new Float32Array(n);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const dx = ((heightSums[px(x + 1, y)] - heightSums[px(x - 1, y)]) / heightScale) * depth;
      const dy = ((heightSums[px(x, y + 1)] - heightSums[px(x, y - 1)]) / heightScale) * depth;
      // Image rows grow downwards, so +y in the image is -Y on the wall.
      let nx = -dx / (2 * mmPerPx);
      let ny = dy / (2 * mmPerPx);
      let nz = 1;
      const length = Math.hypot(nx, ny, nz);
      nx /= length;
      ny /= length;
      nz /= length;
      normal[i * 4] = (nx * 0.5 + 0.5) * 255;
      normal[i * 4 + 1] = (ny * 0.5 + 0.5) * 255;
      normal[i * 4 + 2] = (nz * 0.5 + 0.5) * 255;
      normal[i * 4 + 3] = 255;
      const lit = (nx * sun.x + ny * sun.y + nz * sun.z) / sun.z;
      shade[i] = Math.min(params.maxSunBoost, Math.max(0, lit));
    }
  }

  const ao = tentSumWrap(heightSums, width, height, radiusPx(params.occlusionRadiusMm, pxPerMm));
  const occlusion = new Uint8ClampedArray(n * 4);
  const occlusionFactor = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const surrounding = ao.sums[i] / ao.scale / heightScale;
    const cavity = Math.min(1, Math.max(0, surrounding - relief[i]));
    const value = 1 - params.occlusionStrength * cavity;
    occlusionFactor[i] = value;
    const byte = value * 255;
    occlusion[i * 4] = byte;
    occlusion[i * 4 + 1] = byte;
    occlusion[i * 4 + 2] = byte;
    occlusion[i * 4 + 3] = 255;
  }

  // Heightfield shadow from a sun above the wall: walk each column downwards,
  // carrying the height of the shadow volume cast by everything above. Two
  // laps around the column let the shadow wrap across the bottom/top edge.
  const dropPerPx = (sun.z / sun.y) * mmPerPx;
  const softness = Math.max(params.shadowSoftnessMm * (sun.z / sun.y), 1e-6);
  const shadow = new Float32Array(n);
  for (let x = 0; x < width; x++) {
    let caster = -Infinity;
    for (let step = 0; step < height * 2; step++) {
      const y = step % height;
      const i = y * width + x;
      const surface = relief[i] * depth;
      if (step >= height) {
        const covered = Math.min(1, Math.max(0, (caster - surface) / softness));
        shadow[i] = 1 - params.shadowStrength * covered;
      }
      caster = Math.max(caster - dropPerPx, surface);
    }
  }

  const diffuse = new Uint8ClampedArray(n * 4);
  for (let i = 0; i < n; i++) {
    const light = occlusionFactor[i] * shade[i] * shadow[i];
    diffuse[i * 4] = albedo[i * 4] * light;
    diffuse[i * 4 + 1] = albedo[i * 4 + 1] * light;
    diffuse[i * 4 + 2] = albedo[i * 4 + 2] * light;
    diffuse[i * 4 + 3] = 255;
  }

  return { height: relief, normal, occlusion, diffuse };
};
